import React, {
  ReactElement, ReactNode, useEffect, useState
} from 'react';

import { Controller, useForm, UseFormReturn } from 'react-hook-form';
import { FormControl, FormHelperText, Select as BaseSelect } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import clsx from 'clsx';

import Label from '@/shared/molecules/Label';

import css from './Select.module.css';

interface SelectProps {
  id: string;
  control?: UseFormReturn['control'];
  children?: ReactNode;
  name: string;
  label?: string;
  errors?: UseFormReturn['formState']['errors'];
  trigger?: UseFormReturn['trigger'];
  labelClass?: string;
  defaultValue?: string;
  required?: boolean;
  onBlur?: () => void;
  controlClass?: string;
  onSelectionChange?: (option: NameValuePair) => void;
  autoFocus?: boolean;
}

const Select = ({
  id,
  control,
  children,
  name,
  errors,
  trigger,
  label,
  labelClass,
  defaultValue = '',
  required = false,
  onBlur,
  controlClass,
  onSelectionChange,
  autoFocus
}: SelectProps): ReactElement => {
  const selectErrors = errors?.[id];
  const hasError = Boolean(selectErrors?.type);
  const [value, setSelectValue] = useState('');

  const { control: defaultControl } = useForm();
  const baseControl = control || defaultControl;

  const setValueOnChange = ({
    target
  }: React.ChangeEvent<HTMLInputElement>) => {
    setSelectValue(target.value);
    onSelectionChange?.(target);
  };

  useEffect(() => {
    if (value !== defaultValue) {
      trigger?.(name);
    }
  }, [trigger, name, value, defaultValue]);

  return (
    <>
      <Label className={clsx(labelClass, css.label)} htmlFor={id}>
        {label}
      </Label>
      <FormControl fullWidth error={hasError} className={controlClass}>
        <Controller
          render={({ field }) => (
            <BaseSelect
              {...field}
              aria-label={`Select ${label}. ${required ? 'Required.' : ''}`}
              data-testid={`select-${name}`}
              error={hasError}
              onBlur={onBlur}
              className={`${hasError ? css.error : ''}`}
              variant="outlined"
              displayEmpty
              autoFocus={autoFocus}
              inputProps={{
                'data-testid': `input-select-${name}`,
                id,
                name,
                className: css.select,
                onChange: setValueOnChange
              }}
              IconComponent={({ ...rest }) => (
                <ExpandMoreIcon
                  {...rest}
                  classes={{ root: css.selectBoxIcon }}
                  data-testid="expand-more-icon-select"
                />
              )}
            >
              {React.Children.toArray(children)}
            </BaseSelect>
          )}
          name={name}
          rules={{ required }}
          control={baseControl}
          defaultValue={defaultValue}
        />
        {hasError ? <FormHelperText id="select-helper">Required field</FormHelperText> : null}
      </FormControl>
    </>
  );
};

export default Select;
